import React, { useEffect, useState, useRef } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  LearningPlacementModal,
} from '../../../components';
import { getLearningProfile, submitLearningPlacement } from '../../../util/api';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import LanguageMenu from './TopbarDesktop/LanguageMenu/LanguageMenu';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';
import { useSelector } from 'react-redux';
import { currentUserListingSelector } from '../../../ducks/user.duck';
import { isUserPendingApproval } from '../../../util/helpersFunction';
import { BLOGS } from '../../../util/constants';
import trialImage from '../../../assets/trial-image.png';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

const isBecomeTutorLink = linkConfig => {
  const href = (linkConfig?.href || '').toLowerCase();
  return href.includes('become-a-tutor');
};

const getVisibleCustomLinks = (customLinks, isAuthenticated) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return isAuthenticated ? links.filter(linkConfig => !isBecomeTutorLink(linkConfig)) : links;
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    onLogout,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    onUpdateProfile,
  } = props; 
  const currentUserListing = useSelector(currentUserListingSelector);
  const [mounted, setMounted] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(true);
  useEffect(() => {
    setMounted(true);
  }, []);

  // ── Forced placement questionnaire for independent students ──────────────
  // Shown once right after registration. Non-skippable.
  // Conditions: authenticated + student type + not a family parent/child + no learning profile
  const [showForcedPlacement, setShowForcedPlacement] = useState(false);
  const [forcedPlacementSubmitting, setForcedPlacementSubmitting] = useState(false);
  const [forcedPlacementError, setForcedPlacementError] = useState(null);
  // Ref that latches true once the profile check is done — prevents re-checking on re-renders
  const profileCheckedRef = React.useRef(false);
  // Ref that latches true once the questionnaire is submitted — prevents modal re-appearing
  const placementCompletedRef = React.useRef(false);

  const userId = currentUser?.id?.uuid;
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const protectedData = currentUser?.attributes?.profile?.protectedData || {};
  const isStudentType  = publicData.userType === 'student';
  const isFamilyParent = publicData.isFamilyParent === true;
  const isFamilyChild  = publicData.isFamilyChild  === true;
  const isIndependentBooking = protectedData.independentBookingEnabled === true;

  // Questionnaire is filled by the learner if:
  // 1. Adult / direct student (!isFamilyParent && isStudentType)
  // 2. Independent child or logged-in child learner (!isFamilyParent && isFamilyChild)
  const isEligibleForPlacement = isAuthenticated && !isFamilyParent && (
    isStudentType ||
    isFamilyChild ||
    isIndependentBooking
  );
  const targetParentId = isFamilyChild ? (protectedData.parentUserId || userId) : userId;
  const isIndependentLearner = !isFamilyChild || isIndependentBooking;

  useEffect(() => {
    // Only run once per session — once checked, never re-check
    if (!isEligibleForPlacement || !userId) return;
    if (profileCheckedRef.current) return;
    if (placementCompletedRef.current) return;

    profileCheckedRef.current = true;

    getLearningProfile({ userId, parentId: targetParentId, childId: userId })
      .then(res => {
        // Server returns { success: true, data: <profile|null> }
        // Controller wraps as { success, data } so res.data.data is the profile
        const raw = res?.data?.data ?? res?.data ?? null;
        // raw may be the profile directly or { hasProfile, profile, ... }
        const profile = raw?.profile || (raw?._id ? raw : null) || (raw?.skills ? raw : null);
        const hasProfile = Boolean(profile && (profile._id || profile.skills || profile.childId));
        if (!hasProfile && !placementCompletedRef.current) {
          setShowForcedPlacement(true);
        }
      })
      .catch(() => {
        // On error — do not block the user, silently skip
      });
  }, [userId, isEligibleForPlacement, targetParentId]); // fires once when the user is known

  const handleForcedPlacementSubmit = async payload => {
    setForcedPlacementSubmitting(true);
    setForcedPlacementError(null);
    try {
      await submitLearningPlacement({
        ...payload,
        userId,
        parentId: targetParentId,
        childId: userId,
        isIndependent: isIndependentLearner,
      });
      // Latch completed — prevents modal re-appearing if useEffect somehow re-runs
      placementCompletedRef.current = true;
      setShowForcedPlacement(false);
    } catch (err) {
      setForcedPlacementError(err?.message || 'Failed to save. Please try again.');
      throw err;
    } finally {
      setForcedPlacementSubmitting(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const isPendingApproval = isAuthenticated &&
    !!currentUserListing &&
    currentUserListing.attributes.state === 'pendingApproval' &&
    currentUserListing.attributes?.publicData?.listingType !== BLOGS;

  const isChildPending = isAuthenticated && protectedData.familyChildStatus === 'pending_approval';
  const isChildRejected = isAuthenticated && protectedData.familyChildStatus === 'rejected' && isRejectionModalOpen;

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
      ? 'orders'
      : currentUserHasListings
        ? 'sales'
        : 'orders';

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds, blogflash } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const visibleCustomLinks = getVisibleCustomLinks(config.topbar?.customLinks, isAuthenticated);
  const sortedCustomLinks = sortCustomLinks(visibleCustomLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = mounted && hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : false;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const [blogFlashVisible, setBlogFlashVisible] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  useEffect(() => {
    if (blogflash === '1') {
      setBlogFlashVisible(true);
      redirectToURLWithoutModalState(history, location, 'blogflash');
    }
  }, [blogflash]);

  const handleCloseTrialModal = () => {
    setShowTrialModal(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('hasSeenTrialModal', 'true');
    }
  };

  useEffect(() => {
    // Show trial modal for guests until they dismiss it; never show after signup/login.
    if (typeof window === 'undefined') return;

    if (currentUser) {
      localStorage.setItem('hasSeenTrialModal', 'true');
      setShowTrialModal(false);
      return;
    }

    const hasSeenTrialModal = localStorage.getItem('hasSeenTrialModal');
    if (!hasSeenTrialModal) {
      setShowTrialModal(true);
    }
  // Use the stable user ID string — not the full currentUser object — as the dep.
  // The full object gets a new reference on every Redux state update and would
  // cause this localStorage check to run repeatedly.
  }, [currentUser?.id?.uuid]);

  const showPendingApprovalBanner = blogFlashVisible || isPendingApproval;

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon
        className={css.searchMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );
  return (
    <div className={css.topbarWrapper}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      {showPendingApprovalBanner ? (
        <div className={css.pendingApprovalBanner} role="status" aria-live="polite">
          <span className={css.pendingApprovalText}>
            <FormattedMessage id="Topbar.pendingApprovalBanner" />
          </span>
        </div>
      ) : null}
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <div className={css.mobileActionButtons}>
          <LanguageMenu currentPage={resolvedCurrentPage} isMobile />
          <Button
            rootClassName={css.menu}
            onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon
              className={css.menuIcon}
              ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            />
            {notificationDot}
          </Button>
        </div>
        {/* {mobileSearchButtonMaybe} */}
      </div>
      <div className={css.desktop} suppressHydrationWarning>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <Modal
        id={'trial-modal'}
        className={css.trialModal}
        isOpen={showTrialModal}
        onClose={handleCloseTrialModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.freeTrialWrapper}>
          <div className={css.trialLeftSide}>
            <div className={css.trialHeading}>
              <FormattedMessage id="Topbar.trialHeading" />
            </div>
            <button className={css.trialLessonButton}>
              <IconCard type="lightningicon" />
              <span>
                <FormattedMessage id="Topbar.getTwoFree" />
              </span>
              <IconCard type="lightningicon" />
            </button>
            <div className={css.trialDescription}>
              <FormattedMessage id="Topbar.getTwoShortLessons" />
            </div> 
            <NamedLink name="SearchPage" className={css.getStartButton}>
              <FormattedMessage id="Topbar.startTrial" />
              <IconCard type="doublearrow" />
            </NamedLink>
            <div className={css.noPaymentText}>
              <FormattedMessage id="Topbar.noPayment" />
            </div>
          </div>
          <div className={css.trialRightSide}>
            <img src={trialImage} alt="trial-image" className={css.trialImage} />
          </div>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <Modal
        id="ChildVerificationPendingModal"
        isOpen={isChildPending}
        onClose={() => {}}
        showCloseButton={false}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div style={{ textAlign: 'center', padding: '32px 24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#f3e8ff',
            color: '#7c3aed',
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            fontFamily: 'Outfit, sans-serif'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '12px', fontFamily: 'Outfit, sans-serif' }}>
            Account Pending Approval
          </h2>
          <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#6b7280', marginBottom: '32px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Your student account is linked to your parent's family wallet and is currently pending approval.
            Please wait until your parent approves your profile from their dashboard to start booking lessons.
          </p>
          <button
            onClick={() => onLogout()}
            style={{
              backgroundColor: '#7c3aed',
              color: '#ffffff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.1), 0 2px 4px -1px rgba(124, 58, 237, 0.06)',
              transition: 'all 0.2s'
            }}
          >
            Log Out
          </button>
        </div>
      </Modal>

      <Modal
        id="ChildVerificationRejectedModal"
        isOpen={isChildRejected}
        onClose={() => {}}
        showCloseButton={false}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div style={{ textAlign: 'center', padding: '32px 24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fee2e2',
            color: '#ef4444',
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            fontFamily: 'Outfit, sans-serif'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937', marginBottom: '12px', fontFamily: 'Outfit, sans-serif' }}>
            Request Rejected
          </h2>
          <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#6b7280', marginBottom: '32px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Your link request has been rejected, and your account is not connected to any parent. Your account will now be treated as a normal student account.
          </p>
          <button
            onClick={() => {
              onUpdateProfile({
                protectedData: {
                  familyChildStatus: null,
                },
              })
                .then(() => {
                  setIsRejectionModalOpen(false);
                })
                .catch(err => {
                  alert('Error updating account status. Please try again.');
                });
            }}
            style={{
              backgroundColor: '#7c3aed',
              color: '#ffffff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.1), 0 2px 4px -1px rgba(124, 58, 237, 0.06)',
              transition: 'all 0.2s'
            }}
          >
            OK
          </button>
        </div>
      </Modal>

      <GenericError show={showGenericError} />

      {/* ── Forced placement questionnaire — non-skippable, shown after registration ── */}
      {isEligibleForPlacement && (
        <LearningPlacementModal
          id="ForcedPlacementModal"
          isOpen={showForcedPlacement}
          onClose={() => setShowForcedPlacement(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          forced={true}
          parentId={targetParentId}
          childId={userId}
          isIndependent={true}
          learnerName={
            currentUser?.attributes?.profile?.firstName ||
            currentUser?.attributes?.profile?.displayName ||
            ''
          }
          onSubmitPlacement={handleForcedPlacementSubmit}
          submitInProgress={forcedPlacementSubmitting}
          submitError={forcedPlacementError}
          onSuccess={() => {
            placementCompletedRef.current = true;
            setShowForcedPlacement(false);
          }}
        />
      )}
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
